// Display-only smoothing for the dashboard's line graphs (the meeting of
// 2026-10-08: a percentage the user can fine-tune). The step loads,
// settlements, readings and exports always use the raw readings.
//
// A centred moving average: at 0% the readings are drawn as they are; from 1%
// up, each point is the average of itself and up to SMOOTHING_MAX_REACH
// readings either side (15 readings at 100%). It averages within one load step
// at a time, so the noise in a hold is smoothed but the jump to the next load
// stays sharp (averaging across it would draw each jump as a slow ramp). A gap
// in a line (a blank reading, or hidden unload steps) is never averaged across
// either: each unbroken run is smoothed on its own, and its ends average over
// what they have.

export const SMOOTHING_MAX_REACH = 7;

// Readings either side that a percentage averages over.
export function smoothingReach(percent) {
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  return p === 0 ? 0 : Math.max(1, Math.round((p / 100) * SMOOTHING_MAX_REACH));
}

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

// The values smoothed; null stays null. groups (optional, one per value, e.g.
// the step each reading is in) splits the runs wherever it changes.
export function smoothValues(values, percent, groups = null) {
  const reach = smoothingReach(percent);
  if (!reach) return values.slice();
  const out = new Array(values.length).fill(null);
  const sameGroup = (a, b) => !groups || groups[a] === groups[b];
  let start = 0;
  while (start < values.length) {
    if (!isNumber(values[start])) { start += 1; continue; }
    let end = start;
    while (end + 1 < values.length && isNumber(values[end + 1]) && sameGroup(end, end + 1)) end += 1;
    // Running sums over the run, so each average costs the same however wide.
    const sums = [0];
    for (let i = start; i <= end; i += 1) sums.push(sums[sums.length - 1] + values[i]);
    for (let i = start; i <= end; i += 1) {
      const lo = Math.max(start, i - reach);
      const hi = Math.min(end, i + reach);
      out[i] = (sums[hi - start + 1] - sums[lo - start]) / (hi - lo + 1);
    }
    start = end + 1;
  }
  return out;
}

// Rows (objects, in time order) with the given keys smoothed within each value
// of groupKey (the step, by default); other fields are kept as they are.
// Returns the rows unchanged at 0%.
export function smoothRows(rows, keys, percent, groupKey = 'step') {
  if (!smoothingReach(percent) || !rows.length) return rows;
  const groups = groupKey ? rows.map((r) => r[groupKey]) : null;
  const smoothed = Object.fromEntries(keys.map((k) => [k, smoothValues(rows.map((r) => r[k]), percent, groups)]));
  return rows.map((r, i) => {
    const next = { ...r };
    keys.forEach((k) => { if (r[k] !== undefined) next[k] = smoothed[k][i]; });
    return next;
  });
}

const SMOOTHING_KEY = 'lt-smoothing';

export function readSmoothing() {
  try {
    const n = Number(localStorage.getItem(SMOOTHING_KEY));
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0;
  } catch {
    return 0;
  }
}

export function writeSmoothing(percent) {
  try {
    localStorage.setItem(SMOOTHING_KEY, String(percent));
  } catch {
    // ignore
  }
}
